import React, { useState, useEffect, useRef, useContext } from 'react';
import axios from "../common/axios";
import { Select, Input, Button, Alert } from 'antd';
import CircularLoader from "../common/CircularLoader";
import {TemplateContext} from "../common/TemplateContext";

const { Option } = Select;

const BookmarkLink = ({conceptId, urlHash, tabBrandId, resourceType}) => {
    const {showSnackbar} = useContext(TemplateContext);
    const [linkGroups, setLinkGroups] = useState([]);
    const [links, setLinks] = useState([]);
    const [link, setLink] = useState();
    const [defaultLink, setDefaultLink] = useState();
    const [linkGroupId, setLinkGroupId] = useState();
    const [name, setName] = useState('');
    const [isUpdateLink, setUpdateLink] = useState(false);
    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState();
    const [saving, setSaving] = useState(false);
    const [saveError, setSaveError] = useState();
    const saveErrorRef = useRef();

    const fetchBookmarks = async () => {
        try {
            let mediaId = undefined;
            let locUrlHash = urlHash;
            if(urlHash != undefined && resourceType == 'MEDIA') {
                const urlHashParts = urlHash.split('/');
                if(urlHashParts.length >= 3) {
                    const locMediaId = Number.parseInt(urlHashParts[urlHashParts.length - 1]);
                    if(!isNaN(locMediaId)) {
                        mediaId = Number.parseInt(locMediaId);
                        locUrlHash = '/' + urlHash + '/';
                    }
                }

            }
            const { data } = await axios.get(
                '/web/ui/linking/userLinkGroupsAndLink',
                { params: {conceptId, mediaId, resourceType, urlHash: locUrlHash, tabBrandId} }
            );

            const lgs = data.linkGroups || [];
            const ls = data.links || [];

            setLinkGroups(lgs);
            setLinks(ls);

            let initialGroupId;
            let initialLink = data.defaultLink;

            if (ls.length > 0) {
                initialLink = ls[0];
                initialGroupId = initialLink.linkGroupId;
                setUpdateLink(true);
            } else if (lgs.length > 0) {
                initialGroupId = lgs[0].linkGroupId;
                setUpdateLink(false);
            }

            setDefaultLink(data.defaultLink);
            setLinkGroupId(initialGroupId);
            setLink(initialLink);
            setName(initialLink?.name || '');
        } catch (err) {
            console.error(err);
            setLoadError('Failed to load bookmark data.');
        } finally {
            setLoading(false);
        }
    };


    useEffect(() => {
        fetchBookmarks();
    }, [conceptId, resourceType, urlHash, tabBrandId]);

    useEffect(() => {
        if (saveError && saveErrorRef.current) {
            saveErrorRef.current.focus();
        }
    }, [saveError]);

    const handleSave = async () => {
        setSaveError(null);
        if (!name.trim()) {
            setSaveError('Bookmark name required.');
            return;
        }
        setSaving(true);

        try {
            let result;
            const payload = {
                linkHash: link.hash,
                linkName: name,
                linkGroupId,
                tabBrandId,
            };

            if (isUpdateLink) {
                result = await axios.post('/web/ui/linking/updateLink', {
                    ...payload,
                    linkUrlId: link.linkUrlId,
                });
            } else {
                result = await axios.post('/web/ui/linking/createLink', {
                    ...payload
                });
            }

            if (result.data) {
                const updated = result.data;
                setLink(updated);
                setLinks(prev =>
                    isUpdateLink
                        ? prev.map(l => (l.linkUrlId === updated.linkUrlId ? updated : l))
                        : [...prev, updated]
                );
                if(isUpdateLink) {
                    showSnackbar('Bookmark updated', 'Your bookmark has been updated' , 'success');
                } else {
                    showSnackbar('Bookmark updated', 'Your bookmark has been added' , 'success');
                }
                setUpdateLink(true);
                window.dispatchEvent(new Event('bookmarkUpdated'));
            }
        } catch (err) {
            console.error(err);
            setSaveError('Error saving bookmark.');
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return (
            <CircularLoader />
        );
    }

    if (loadError) {
        return (
            <Alert
                showIcon={true}
                role="alert"
                message={loadError}
                type="error"
                closable={false}
            />
        );
    }

    const labelStyle = { minWidth: 80};

    return (
        <div>
            {saveError && (
                <div tabIndex={-1} ref={saveErrorRef}>
                    <Alert role="alert" message={saveError} type="error" showIcon />
                </div>
            )}

            {linkGroups.length > 0 && (
                <div className="flexAlignCentre mediumGap margin-b--m">
                    <label htmlFor="folder-select" style={labelStyle}>
                        Folder:
                    </label>
                    <Select
                        id="folder-select"
                        className="flexMax"
                        value={linkGroupId}
                        onChange={value => {
                            setLinkGroupId(value);
                            setSaveError(null);
                            const existingLink = links.find(l => l.linkGroupId === value) || null;
                            if (existingLink) {
                                setLink(existingLink);
                                setName(existingLink.name || '');
                                setUpdateLink(true);
                            } else {
                                setLink(defaultLink);
                                setName(defaultLink.name || '');
                                setUpdateLink(false);
                            }
                        }}
                    >
                        {linkGroups.map(g => (
                            <Option key={g.linkGroupId} value={g.linkGroupId}>
                                {g.name}
                            </Option>
                        ))}
                    </Select>
                </div>
            )}

            <div className="flexAlignCentre mediumGap margin-b--m">
                <label htmlFor="name-input" style={labelStyle}>
                    Name:
                </label>
                <Input
                    id="name-input"
                    required
                    placeholder="Bookmark name"
                    value={name}
                    className="flexMax"
                    onChange={e => {
                        setName(e.target.value);
                        setSaveError(null);
                    }}
                />
            </div>

            <div className="flexAlignCentre mediumGap margin-b--m" role="group" aria-labelledby="location-label" >
                <span id="location-label" style={labelStyle}>
                  Location:
                </span>
                <div>
                    {link?.conceptName}
                    {link?.tabBean && <> → {link.tabBean.tabName}</>}
                </div>
            </div>

            <div className="flexJustifyStart flexAlignCentre mediumGap margin-b--m">
                <Button
                    id="save-button"
                    type="primary"
                    htmlType="button"
                    disabled={saving}
                    onClick={handleSave}
                >
                    {isUpdateLink ? 'Update' : 'Add'}
                </Button>
                <CircularLoader visible={saving} pageCentered={false} displayInElement={true}/>
            </div>
        </div>
    );
};

export default BookmarkLink;
